import * as z from 'zod';
import { suretyTypeSchema } from '../../../../enums.mjs';
import { Currency } from '../../../../types/currency/currency.mjs';
import { isoDateTimeSchema, suretyAccountBondingLineId, rateSchemaV2, bondingLinesVersion, carrierName } from '../../../../types/opaque.mjs';
import { UpdatedBy, schema, singleExceedsAggregateMessage } from '../../../../types/surety/account/bonding-line.mjs';
import { nonNegativeCurrencySchema, currencySchema } from '../../../../types/utils.mjs';

const BONDING_LINES_MEDIA_TYPE = "application/vnd.inrev.bonding-lines+json";
const limitsSchema = z.object({
    single: nonNegativeCurrencySchema.transform(Currency.round),
    aggregate: nonNegativeCurrencySchema.transform(Currency.round),
});
var Get;
(function (Get) {
    (function (Response) {
        const updatedBySchema = z.discriminatedUnion("type", [
            UpdatedBy.userSchema.extend({ name: z.string(), at: isoDateTimeSchema }),
            UpdatedBy.systemSchema.extend({ at: isoDateTimeSchema }),
        ]);
        Response.schema = z.object({
            version: bondingLinesVersion.nullable(),
            published: z.boolean(),
            limits: limitsSchema.nullable(),
            used: currencySchema,
            updatedBy: updatedBySchema.nullable(),
            lines: z.array(z.object({
                suretyType: suretyTypeSchema,
                name: z.string(),
                used: currencySchema,
                line: z
                    .object({
                    id: suretyAccountBondingLineId,
                    rate: rateSchemaV2.nullable(),
                    single: currencySchema.nullable(),
                    aggregate: currencySchema.nullable(),
                    updatedAt: isoDateTimeSchema,
                })
                    .nullable(),
            })),
        });
    })(Get.Response || (Get.Response = {}));
})(Get || (Get = {}));
var Create;
(function (Create) {
    (function (Request) {
        const lineShape = schema.shape;
        Request.baseSchema = z.object({
            lines: z
                .array(z
                .object({
                suretyType: suretyTypeSchema,
                rate: lineShape.rate,
                single: lineShape.singleLimit,
                aggregate: lineShape.aggregateLimit,
            })
                .refine((line) => line.single <= line.aggregate, {
                path: ["single"],
                error: singleExceedsAggregateMessage,
            }))
                .refine((lines) => new Set(lines.map((line) => line.suretyType)).size === lines.length, {
                error: "Each surety type can be listed once",
            }),
            version: bondingLinesVersion,
        });
        Request.schema = Request.baseSchema.extend({ limits: limitsSchema.optional() });
    })(Create.Request || (Create.Request = {}));
})(Create || (Create = {}));
var AllowedCarriers;
(function (AllowedCarriers) {
    (function (Get) {
        (function (Response) {
            Response.schema = z.array(z.object({
                carrierName,
                displayName: z.string(),
                priority: z.number(),
                enabled: z.boolean(),
                singleAuthority: currencySchema,
                aggregateAuthority: currencySchema,
                tListing: currencySchema.nullable(),
            }));
        })(Get.Response || (Get.Response = {}));
    })(AllowedCarriers.Get || (AllowedCarriers.Get = {}));
})(AllowedCarriers || (AllowedCarriers = {}));
var ErrorDetails;
(function (ErrorDetails) {
    const lineLimitFieldSchema = z.enum(["single", "aggregate"]);
    (function (LineExceedsLimits) {
        LineExceedsLimits.schema = z.object({
            suretyType: suretyTypeSchema,
            field: lineLimitFieldSchema,
            value: currencySchema,
            limit: currencySchema,
        });
    })(ErrorDetails.LineExceedsLimits || (ErrorDetails.LineExceedsLimits = {}));
    (function (LimitsInvalid) {
        LimitsInvalid.schema = z.object({
            field: lineLimitFieldSchema,
            suretyType: suretyTypeSchema.optional(),
        });
    })(ErrorDetails.LimitsInvalid || (ErrorDetails.LimitsInvalid = {}));
    (function (BondingLineExists) {
        BondingLineExists.schema = z.object({ suretyType: suretyTypeSchema });
    })(ErrorDetails.BondingLineExists || (ErrorDetails.BondingLineExists = {}));
})(ErrorDetails || (ErrorDetails = {}));

export { AllowedCarriers, BONDING_LINES_MEDIA_TYPE, Create, ErrorDetails, Get, limitsSchema };
